/**
 * ProcurementPage.jsx — Procurement Filing & Multi-Level Approval
 *
 * Fixes applied:
 *  - Items table: description column fills width properly, price is a plain
 *    text input (no browser spinner) that accepts decimal values correctly
 *  - Description textarea: controlled component with working onChange
 *  - Formal letter: ALS logo from /als-logo.png, proper letter format with
 *    ref number, date, subject, body paragraph, items table, audit trail,
 *    and three-column signature block
 *  - Attachments: visible in detail view for principal and superadmin roles
 *  - Campus scoping: teacher → campus admin, admin → campus principal,
 *    principal → superadmin; superadmin sees all campuses
 */

import React, { useState, useCallback, useRef } from "react";
import {
  MdAdd,
  MdSearch,
  MdRefresh,
  MdAttachFile,
  MdClose,
  MdPrint,
  MdSend,
  MdDescription,
  MdCheckCircle,
  MdCancel,
  MdUndo,
  MdOutlineBarChart,
  MdFilterList,
} from "react-icons/md";
import { FiFileText } from "react-icons/fi";
import { API_BASE_URL, getAuthHeaders, getJsonAuthHeaders } from "../../../utils/api";
import "./procurement.css";

// ── Helpers ────────────────────────────────────────────────────────────────────

const STATUS_LABELS = {
  draft:               "Draft",
  pending_admin:       "Pending Admin",
  pending_principal:   "Pending Principal",
  pending_superadmin:  "Pending Superadmin",
  approved:            "Approved",
  rejected:            "Rejected",
  returned:            "Returned",
};

const TRACK_STEPS = ["Filed", "Admin", "Principal", "Approved"];

function statusToStep(status) {
  if (status === "rejected" || status === "returned") return -1;
  if (status === "draft")              return 0;
  if (status === "pending_admin")      return 1;
  if (status === "pending_principal")  return 2;
  if (status === "pending_superadmin") return 3;
  if (status === "approved")           return 4;
  return 0;
}

const fmtPKR = (n) =>
  "PKR\u00A0" + Number(n || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 });

const fmtDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-PK", {
    day: "2-digit", month: "long", year: "numeric",
  });
};

const fmtDateTime = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-PK", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

// ── StatusBadge ───────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const cls = {
    draft:               "draft",
    pending_admin:       "pending_admin",
    pending_principal:   "pending_principal",
    pending_superadmin:  "pending_superadmin",
    approved:            "approved",
    rejected:            "rejected",
    returned:            "returned",
  }[status] || "draft";
  return <span className={`proc-badge ${cls}`}>{STATUS_LABELS[status] || status}</span>;
}

// ── TrackDots ─────────────────────────────────────────────────────────────────

function TrackDots({ status }) {
  const step = statusToStep(status);
  const isRejected = status === "rejected" || status === "returned";
  return (
    <div className="proc-card-track">
      {TRACK_STEPS.map((label, i) => {
        let cls = "";
        if (isRejected)    cls = i === 0 ? "done" : i === 1 ? "reject" : "";
        else               cls = i < step ? "done" : i === step ? "active" : "";
        return (
          <div key={label} className="proc-track-step">
            {i > 0 && <div className={`proc-track-line${i <= step && !isRejected ? " done" : ""}`} />}
            <div className={`proc-track-dot ${cls}`} title={label} />
          </div>
        );
      })}
    </div>
  );
}

// ── AuditTrail ────────────────────────────────────────────────────────────────

function AuditTrail({ entries }) {
  if (!entries || entries.length === 0)
    return <p style={{ fontSize: ".83rem", color: "#9aa5b4" }}>No activity yet.</p>;
  return (
    <div className="proc-audit-list">
      {[...entries].reverse().map((e, i) => {
        const isReject = (e.action || "").toLowerCase().includes("reject");
        const isReturn = (e.action || "").toLowerCase().includes("return");
        return (
          <div key={i} className="proc-audit-entry">
            <div className={`proc-audit-dot${isReject ? " reject" : isReturn ? " return" : ""}`}>
              {isReject ? "✕" : isReturn ? "↩" : "✓"}
            </div>
            <div className="proc-audit-content">
              <div className="proc-audit-action">{e.action}</div>
              <div className="proc-audit-actor">{e.actor_name} · {e.actor_role}</div>
              <div className="proc-audit-time">{fmtDateTime(e.timestamp)}</div>
              {e.remark && <div className="proc-audit-remark">"{e.remark}"</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── NewRequestModal ───────────────────────────────────────────────────────────

function NewRequestModal({ role, onClose, onSaved, showToast }) {
  const [items, setItems] = useState([{ item: "", price: "" }]);
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showLetterEditor, setShowLetterEditor] = useState(false);
  const fileRef = useRef();

  // Destination is fixed by role — teachers always go to admin, etc.
  // Superadmin cannot send to themselves; they don't create requests.
  const sendTo =
    role === "teacher"                             ? "admin"
    : role === "admin" || role === "administrator" ? "principal"
    : "superadmin";   // principal → superadmin

  const sendToLabel =
    sendTo === "admin"       ? "Admin (Campus)"
    : sendTo === "principal" ? "Principal (Campus)"
    : "Superadmin";

  const roleTitle = role === "teacher" ? "Teacher"
    : role === "admin" || role === "administrator" ? "Admin"
    : role === "principal" ? "Principal"
    : "User";

  // Default letter body (salutation and closing are kept separate in FormalLetter)
  const defaultLetterBody =
    `I, ${roleTitle}, am writing to formally request the procurement of the items listed below for the campus. The items are required due to operational needs and their timely availability is essential for the smooth functioning of the institution.\n\nI humbly request your kind approval for the above procurement at the earliest convenience.\n\nYours sincerely,\n${roleTitle}`;

  const [letterBody, setLetterBody] = useState(defaultLetterBody);

  const total = items.reduce((s, it) => s + (parseFloat(it.price) || 0), 0);

  const updateItem = (idx, field, val) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: val } : it)));

  const addItem = () => setItems((prev) => [...prev, { item: "", price: "" }]);

  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const handleFileChange = (e) => {
    setFiles((prev) => [...prev, ...Array.from(e.target.files || [])]);
    e.target.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim()) {
      showToast("error", "Validation", "Subject is required.");
      return;
    }
    const filledItems = items.filter((it) => it.item.trim() && parseFloat(it.price) > 0);
    if (filledItems.length === 0) {
      showToast("error", "Validation", "Add at least one item with a valid price.");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/procurement`, {
        method: "POST",
        headers: getJsonAuthHeaders(),
        body: JSON.stringify({
          subject: subject.trim(),
          description: description.trim() || null,
          items: filledItems.map((it) => ({
            item: it.item.trim(),
            price: parseFloat(it.price),
          })),
          send_to: sendTo,
          letter_body: letterBody.trim() || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to create request");
      }
      const created = await res.json();

      // Upload attachments sequentially
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        await fetch(`${API_BASE_URL}/api/procurement/${created.id}/attachments`, {
          method: "POST",
          headers: getAuthHeaders(),
          body: fd,
        }).catch(() => {});
      }

      showToast(
        "success",
        "Request Submitted",
        `Procurement request ${created.ref_number} sent to ${sendToLabel}.`
      );
      onSaved();
      onClose();
    } catch (err) {
      showToast("error", "Error", err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="proc-modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="proc-modal" style={{ maxWidth: 700 }}>
        {/* ── Header ── */}
        <div className="proc-modal-header">
          <div>
            <h3 className="proc-modal-title">New Procurement Request</h3>
            <p className="proc-modal-sub">
              Enter items and prices, then send the request to{" "}
              <strong>{sendToLabel}</strong> for review.
            </p>
          </div>
          <button className="proc-modal-close" onClick={onClose} aria-label="Close">
            <MdClose />
          </button>
        </div>

        <form onSubmit={handleSubmit} autoComplete="off">

          {/* ── Items & Prices ── */}
          <div className="proc-form-group">
            <label className="proc-form-label">
              ITEMS &amp; PRICES <span>*</span>
            </label>

            {/* Header row */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "32px 1fr 140px 32px",
              gap: "6px",
              padding: "6px 4px",
              background: "var(--proc-gray-50)",
              borderRadius: "6px 6px 0 0",
              borderBottom: "1px solid var(--proc-gray-200)",
              marginBottom: 0,
            }}>
              <span style={{ fontSize: ".72rem", color: "var(--proc-gray-600)", fontWeight: 700, textTransform: "uppercase", paddingTop: 4 }}>#</span>
              <span style={{ fontSize: ".72rem", color: "var(--proc-gray-600)", fontWeight: 700, textTransform: "uppercase", paddingTop: 4 }}>Item Description</span>
              <span style={{ fontSize: ".72rem", color: "var(--proc-gray-600)", fontWeight: 700, textTransform: "uppercase", paddingTop: 4 }}>Price (PKR)</span>
              <span />
            </div>

            {/* Item rows */}
            <div style={{
              border: "1.5px solid var(--proc-gray-200)",
              borderTop: "none",
              borderRadius: "0 0 6px 6px",
              overflow: "hidden",
            }}>
              {items.map((it, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "32px 1fr 140px 32px",
                    gap: "6px",
                    padding: "6px 4px",
                    alignItems: "center",
                    borderBottom: idx < items.length - 1 ? "1px solid var(--proc-gray-100)" : "none",
                  }}
                >
                  <span style={{
                    fontSize: ".8rem", color: "var(--proc-gray-400)",
                    fontWeight: 700, textAlign: "center",
                  }}>
                    {idx + 1}
                  </span>

                  {/* Description — plain text, fills column */}
                  <input
                    type="text"
                    className="proc-input"
                    placeholder="e.g. UPS 1200VA"
                    value={it.item}
                    onChange={(e) => updateItem(idx, "item", e.target.value)}
                    style={{ padding: ".42rem .7rem", fontSize: ".88rem" }}
                  />

                  {/* Price — text input styled to look clean, no spinner */}
                  <input
                    type="text"
                    inputMode="decimal"
                    className="proc-input"
                    placeholder="0.00"
                    value={it.price}
                    onChange={(e) => {
                      // Allow only digits and one decimal point
                      const raw = e.target.value.replace(/[^0-9.]/g, "");
                      const parts = raw.split(".");
                      const cleaned =
                        parts.length > 2
                          ? parts[0] + "." + parts.slice(1).join("")
                          : raw;
                      updateItem(idx, "price", cleaned);
                    }}
                    style={{
                      padding: ".42rem .7rem",
                      fontSize: ".88rem",
                      textAlign: "right",
                    }}
                  />

                  {/* Remove button */}
                  <button
                    type="button"
                    onClick={() => removeItem(idx)}
                    disabled={items.length === 1}
                    aria-label="Remove item"
                    style={{
                      background: "none",
                      border: "none",
                      cursor: items.length === 1 ? "default" : "pointer",
                      color: items.length === 1 ? "var(--proc-gray-200)" : "var(--proc-red)",
                      padding: "4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <MdClose size={16} />
                  </button>
                </div>
              ))}
            </div>

            <button type="button" className="proc-add-item-btn" onClick={addItem} style={{ marginTop: ".6rem" }}>
              <MdAdd size={16} /> Add item
            </button>
          </div>

          {/* ── Total bar ── */}
          <div className="proc-total-row" style={{ marginTop: ".4rem" }}>
            TOTAL REQUESTED
            <span>{fmtPKR(total)}</span>
          </div>

          {/* ── Subject ── */}
          <div className="proc-form-group">
            <label className="proc-form-label">SUBJECT <span>*</span></label>
            <input
              type="text"
              className="proc-input"
              placeholder="e.g. Request for procurement of New UPS"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
            />
          </div>

          {/* ── Description ── */}
          <div className="proc-form-group">
            <label className="proc-form-label">
              DESCRIPTION{" "}
              <span style={{ color: "var(--proc-gray-400)", fontWeight: 400, textTransform: "none" }}>
                (optional)
              </span>
            </label>
            <textarea
              className="proc-textarea"
              placeholder="Explain why this procurement is needed, any urgency, or additional context..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              style={{ width: "100%", boxSizing: "border-box", resize: "vertical" }}
            />
          </div>

          {/* ── Letter to Recipient (editable, forwarded with request) ── */}
          <div className="proc-form-group">
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              marginBottom: ".45rem",
            }}>
              <label className="proc-form-label" style={{ margin: 0 }}>
                LETTER TO {sendToLabel.toUpperCase()}
                {" "}<span style={{ color: "var(--proc-gray-400)", fontWeight: 400, textTransform: "none" }}>
                  (forwarded with request)
                </span>
              </label>
              <button
                type="button"
                onClick={() => setShowLetterEditor((p) => !p)}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  fontSize: ".8rem", color: "var(--proc-purple)", fontWeight: 600,
                  padding: ".2rem .4rem", borderRadius: 4,
                }}
              >
                {showLetterEditor ? "▲ Hide Letter" : "▼ Show / Edit Letter"}
              </button>
            </div>

            {showLetterEditor && (
              <div style={{
                border: "1.5px solid var(--proc-gray-200)",
                borderRadius: "var(--proc-radius-sm)",
                overflow: "hidden",
              }}>
                {/* ── Editable textarea for letter body ── */}
                <div style={{
                  background: "var(--proc-gray-50)",
                  borderBottom: "1px solid var(--proc-gray-200)",
                  padding: ".75rem 1rem",
                }}>
                  <div style={{ fontSize: ".72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--proc-gray-600)", marginBottom: ".55rem" }}>
                    Edit Letter Body
                  </div>
                  <textarea
                    className="proc-textarea"
                    value={letterBody}
                    onChange={(e) => setLetterBody(e.target.value)}
                    rows={6}
                    style={{
                      width: "100%", boxSizing: "border-box",
                      fontFamily: "'Times New Roman', Georgia, serif",
                      fontSize: ".9rem", lineHeight: 1.85,
                      padding: ".7rem .85rem",
                      resize: "vertical",
                    }}
                    placeholder="Write your formal letter body here..."
                  />
                </div>

                {/* ── Full ALS letterhead live preview ── */}
                <div style={{ padding: "1.2rem 1.4rem", background: "#fff" }}>
                  <div style={{ fontSize: ".7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--proc-gray-400)", marginBottom: ".7rem" }}>
                    Letter Preview
                  </div>

                  {/* Letterhead */}
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                    <img
                      src="/als-logo.png"
                      alt="ALS"
                      style={{ width: 54, height: 54, objectFit: "contain", flexShrink: 0 }}
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                    <div style={{ flex: 1, textAlign: "center" }}>
                      <div style={{ fontFamily: "'Times New Roman', serif", fontSize: 17, fontWeight: "bold" }}>
                        AMERICAN LYCETUFF SCHOOLS
                      </div>
                      <div style={{ fontFamily: "'Times New Roman', serif", fontSize: 11, color: "#666", marginTop: 2 }}>
                        Procurement Request Letter
                      </div>
                    </div>
                  </div>
                  <hr style={{ border: "none", borderTop: "2px solid #111", margin: "6px 0 10px" }} />

                  {/* Ref & Date */}
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, fontFamily: "'Times New Roman', serif", marginBottom: 8 }}>
                    <span><strong>Ref No:</strong> (auto-assigned)</span>
                    <span><strong>Date:</strong> {new Date().toLocaleDateString("en-PK", { day: "2-digit", month: "long", year: "numeric" })}</span>
                  </div>

                  {/* Subject */}
                  <div style={{ fontFamily: "'Times New Roman', serif", fontSize: 13, marginBottom: 10 }}>
                    <strong>Subject:</strong>{" "}
                    <span style={{ textDecoration: "underline", fontWeight: "bold" }}>
                      {subject || "(enter subject above)"}
                    </span>
                  </div>

                  {/* Salutation */}
                  <div style={{ fontFamily: "'Times New Roman', serif", fontSize: 13, lineHeight: 1.9, marginBottom: 8 }}>
                    Respected Sir / Madam,
                  </div>

                  {/* Body */}
                  <div style={{
                    fontFamily: "'Times New Roman', serif", fontSize: 13,
                    lineHeight: 1.9, marginBottom: 8,
                    whiteSpace: "pre-wrap", color: "#222",
                  }}>
                    {letterBody || "(letter body will appear here)"}
                  </div>

                  {/* Items placeholder */}
                  <div style={{
                    fontFamily: "'Times New Roman', serif", fontSize: 12,
                    color: "#888", fontStyle: "italic", marginBottom: 12,
                    borderLeft: "3px solid #ddd", paddingLeft: 8,
                  }}>
                    [Items &amp; prices table will be inserted here automatically]
                  </div>

                  {/* Signature */}
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 36 }}>
                    {["Filed By", "Principal", "Superadmin"].map((label, i) => (
                      <div key={i} style={{ textAlign: "center", width: "30%" }}>
                        <div style={{ borderTop: "1px solid #111", marginBottom: 4 }} />
                        <div style={{ fontFamily: "'Times New Roman', serif", fontSize: 11, fontWeight: "bold" }}>{label}</div>
                        {i === 0 && <div style={{ fontFamily: "'Times New Roman', serif", fontSize: 10, color: "#666" }}>{roleTitle}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {!showLetterEditor && (
              <p style={{ fontSize: ".77rem", color: "var(--proc-gray-400)", margin: ".1rem 0 0" }}>
                A formal letter is pre-written and will be forwarded with this request.{" "}
                <span
                  style={{ color: "var(--proc-purple)", cursor: "pointer", textDecoration: "underline" }}
                  onClick={() => setShowLetterEditor(true)}
                >
                  Click to view or edit
                </span>
              </p>
            )}
          </div>

          {/* ── Attachments ── */}
          <div className="proc-form-group">
            <label className="proc-form-label">
              QUOTATIONS / DOCUMENTS{" "}
              <span style={{ color: "var(--proc-gray-400)", fontWeight: 400, textTransform: "none" }}>
                (optional, multiple allowed)
              </span>
            </label>
            <input
              type="file"
              ref={fileRef}
              style={{ display: "none" }}
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xlsx,.xls"
              onChange={handleFileChange}
            />
            <button
              type="button"
              className="proc-file-upload-label"
              onClick={() => fileRef.current?.click()}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}
            >
              <MdAttachFile size={16} /> Choose Files
            </button>
            <span style={{ marginLeft: 10, fontSize: ".82rem", color: "var(--proc-gray-400)" }}>
              {files.length === 0 ? "No file chosen" : `${files.length} file(s) selected`}
            </span>
            {files.length > 0 && (
              <div className="proc-file-list" style={{ marginTop: ".5rem" }}>
                {files.map((f, i) => (
                  <div key={i} className="proc-file-item">
                    <FiFileText size={13} />
                    <span style={{ flex: 1 }}>{f.name}</span>
                    <button
                      type="button"
                      className="proc-file-remove"
                      onClick={() => setFiles((prev) => prev.filter((_, fi) => fi !== i))}
                    >
                      <MdClose size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <p style={{ fontSize: ".74rem", color: "var(--proc-gray-400)", marginTop: ".35rem" }}>
              Supported: PDF, Word, Excel, JPG, PNG
            </p>
          </div>

          {/* ── Submit bar ── */}
          <div style={{
            display: "flex", justifyContent: "flex-end", gap: ".7rem",
            marginTop: "1.2rem", paddingTop: "1rem",
            borderTop: "1px solid var(--proc-gray-200)",
          }}>
            <button
              type="button"
              className="proc-btn proc-btn-outline"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="proc-btn proc-btn-primary"
              disabled={saving}
            >
              <MdSend size={15} />
              {saving ? "Sending…" : `Send to ${sendToLabel}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── FormalLetter (printable, editable body) ──────────────────────────────────
// editableContent: { salutation, body, closing } — optional overrides
// showAuditTrail is intentionally NOT included in the letter (removed per requirement)

function FormalLetter({ request, editableContent = {} }) {
  // Logo served from React's public folder — always accessible as /als-logo.png
  const logoUrl = "/als-logo.png";
  const today = fmtDate(new Date().toISOString());

  // letter_body stores the editable body text (no salutation/closing baked in)
  const letterBodyText = editableContent.body !== undefined
    ? editableContent.body
    : (request.letter_body || "The procurement request details are provided below for your kind review and approval.");

  const salutation = editableContent.salutation !== undefined
    ? editableContent.salutation
    : "Respected Sir / Madam,";

  const closing = editableContent.closing !== undefined
    ? editableContent.closing
    : "Kindly process the above procurement as per the approved amount and institutional guidelines.";

  return (
    <div id="proc-formal-letter" style={{ fontFamily: "'Times New Roman', serif", color: "#111", padding: "0" }}>
      {/* ── Letterhead ── */}
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 0 }}>
        <tbody>
          <tr>
            <td style={{ width: 80, verticalAlign: "middle", paddingRight: 16 }}>
              <img
                src={logoUrl}
                alt="ALS Logo"
                style={{ width: 70, height: 70, objectFit: "contain" }}
                onError={(e) => { e.target.style.display = "none"; }}
              />
            </td>
            <td style={{ verticalAlign: "middle", textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: "bold", letterSpacing: ".02em" }}>
                AMERICAN LYCETUFF SCHOOLS
              </div>
              <div style={{ fontSize: 12, color: "#444", marginTop: 3 }}>
                {request.campus_name || "Campus"} — Procurement Approval Letter
              </div>
            </td>
            <td style={{ width: 80 }} />
          </tr>
        </tbody>
      </table>
      <hr style={{ border: "none", borderTop: "2.5px solid #111", margin: "10px 0 14px" }} />

      {/* ── Meta row ── */}
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginBottom: 10 }}>
        <tbody>
          <tr>
            <td style={{ width: "50%" }}>
              <strong>Ref No:</strong> {request.ref_number}
            </td>
            <td style={{ textAlign: "right" }}>
              <strong>Date:</strong> {today}
            </td>
          </tr>
        </tbody>
      </table>

      {/* ── Subject ── */}
      <p style={{ fontSize: 13, marginBottom: 16 }}>
        <strong>Subject:</strong>{" "}
        <span style={{ textDecoration: "underline", fontWeight: "bold" }}>
          {request.subject}
        </span>
      </p>

      {/* ── Salutation & body ── */}
      <p style={{ fontSize: 13, lineHeight: 1.9, marginBottom: 14 }}>{salutation}</p>
      <p style={{ fontSize: 13, lineHeight: 1.9, marginBottom: 14, whiteSpace: "pre-wrap" }}>{letterBodyText}</p>

      {/* ── Items table ── */}
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 16, fontSize: 13 }}>
        <thead>
          <tr style={{ background: "#f5f5f5" }}>
            <th style={{ border: "1px solid #bbb", padding: "6px 10px", textAlign: "left", width: 40 }}>#</th>
            <th style={{ border: "1px solid #bbb", padding: "6px 10px", textAlign: "left" }}>Item Description</th>
            <th style={{ border: "1px solid #bbb", padding: "6px 10px", textAlign: "right", width: 130 }}>Price (PKR)</th>
          </tr>
        </thead>
        <tbody>
          {(request.items || []).map((it, i) => (
            <tr key={i}>
              <td style={{ border: "1px solid #bbb", padding: "5px 10px", textAlign: "center" }}>{i + 1}</td>
              <td style={{ border: "1px solid #bbb", padding: "5px 10px" }}>{it.item}</td>
              <td style={{ border: "1px solid #bbb", padding: "5px 10px", textAlign: "right" }}>
                {Number(it.price).toLocaleString("en-PK")}
              </td>
            </tr>
          ))}
          <tr>
            <td colSpan={2} style={{ border: "1px solid #bbb", padding: "6px 10px", fontWeight: "bold", textAlign: "right" }}>
              Total Requested
            </td>
            <td style={{ border: "1px solid #bbb", padding: "6px 10px", fontWeight: "bold", textAlign: "right" }}>
              {Number(request.total_amount).toLocaleString("en-PK")}
            </td>
          </tr>
          {request.approved_amount != null && (
            <tr>
              <td colSpan={2} style={{ border: "1px solid #bbb", padding: "6px 10px", fontWeight: "bold", textAlign: "right", color: "#1a7c4b" }}>
                Approved Amount
              </td>
              <td style={{ border: "1px solid #bbb", padding: "6px 10px", fontWeight: "bold", textAlign: "right", color: "#1a7c4b" }}>
                {Number(request.approved_amount).toLocaleString("en-PK")}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* ── Description if any ── */}
      {request.description && (
        <p style={{ fontSize: 13, lineHeight: 1.8, marginBottom: 14 }}>
          <strong>Additional Details:</strong> {request.description}
        </p>
      )}

      {/* ── Closing ── */}
      <p style={{ fontSize: 13, lineHeight: 1.9, marginBottom: 6 }}>{closing}</p>
      <p style={{ fontSize: 13, lineHeight: 1.9, marginBottom: 40 }}>Regards,</p>

      {/* ── Signature block ── (NO audit trail in letter) ── */}
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 10 }}>
        <tbody>
          <tr>
            <td style={{ textAlign: "center", width: "33%", paddingTop: 40 }}>
              <div style={{ borderTop: "1px solid #111", width: 160, margin: "0 auto 8px" }} />
              <div style={{ fontSize: 12, fontWeight: "bold" }}>Filed By</div>
              <div style={{ fontSize: 11, color: "#444" }}>{request.created_by_name}</div>
              <div style={{ fontSize: 11, color: "#444" }}>({request.created_by_role})</div>
            </td>
            <td style={{ textAlign: "center", width: "33%", paddingTop: 40 }}>
              <div style={{ borderTop: "1px solid #111", width: 160, margin: "0 auto 8px" }} />
              <div style={{ fontSize: 12, fontWeight: "bold" }}>Principal</div>
              <div style={{ fontSize: 11, color: "#444" }}>{request.campus_name || ""}</div>
            </td>
            <td style={{ textAlign: "center", width: "33%", paddingTop: 40 }}>
              <div style={{ borderTop: "1px solid #111", width: 160, margin: "0 auto 8px" }} />
              <div style={{ fontSize: 12, fontWeight: "bold" }}>Superadmin</div>
              <div style={{ fontSize: 11, color: "#444" }}>American Lycetuff Schools</div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ── DetailModal ───────────────────────────────────────────────────────────────

function DetailModal({ request, role, onClose, onRefresh, showToast }) {
  const [approvedAmount, setApprovedAmount] = useState(
    request.approved_amount != null
      ? String(request.approved_amount)
      : String(request.total_amount)
  );
  const [remark, setRemark] = useState("");
  const [acting, setActing] = useState(false);
  const [showLetter, setShowLetter] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  // Editable letter content — user can customise before printing
  // superadmin gets view-only (no edit fields shown)
  const defaultBody = request.letter_body
    || "The procurement request details are provided below for your kind review and approval.";
  const [letterEdits, setLetterEdits] = useState({
    salutation: "Respected Sir / Madam,",
    body: defaultBody,
    closing: "Kindly process the above procurement as per the approved amount and institutional guidelines.",
  });

  // Helper to build authenticated URL for a stored attachment
  const attUrl = (att) =>
    `${API_BASE_URL}/api/procurement/attachment/${encodeURIComponent(att.path)}`;

  // Open an attachment with auth headers — avoids "Not authenticated" on direct URL
  const openAttachment = async (att) => {
    try {
      const res = await fetch(attUrl(att), { headers: getAuthHeaders() });
      if (!res.ok) throw new Error("Failed to load file");
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      // Images open in new tab; other files download with original filename
      if (isImage(att)) {
        a.target = "_blank";
        a.rel = "noopener";
        a.click();
      } else {
        a.download = att.filename || "attachment";
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (err) {
      showToast("error", "Cannot Open File", err.message);
    }
  };

  // Fetch blob URL for inline image display (with auth)
  const [imageBlobUrls, setImageBlobUrls] = React.useState({});
  React.useEffect(() => {
    const atts = request.attachments || [];
    const imgs = atts.filter(isImage);
    if (imgs.length === 0) return;
    let cancelled = false;
    (async () => {
      const entries = {};
      for (const att of imgs) {
        try {
          const res = await fetch(attUrl(att), { headers: getAuthHeaders() });
          if (!res.ok) continue;
          const blob = await res.blob();
          entries[att.path] = URL.createObjectURL(blob);
        } catch (_) {}
      }
      if (!cancelled) setImageBlobUrls(entries);
    })();
    return () => {
      cancelled = true;
      // Revoke on unmount
      Object.values(imageBlobUrls).forEach((u) => URL.revokeObjectURL(u));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.id]);

  const isImage = (att) => {
    const mime = (att.mime || "").toLowerCase();
    const ext  = (att.filename || "").toLowerCase();
    return (
      mime.startsWith("image/") ||
      ext.endsWith(".jpg") || ext.endsWith(".jpeg") ||
      ext.endsWith(".png") || ext.endsWith(".webp") || ext.endsWith(".gif")
    );
  }

  // Who can act at the current status
  const canAct = (() => {
    const s = request.status;
    if (role === "admin" || role === "administrator") return s === "pending_admin";
    if (role === "principal")  return s === "pending_principal";
    if (role === "superadmin") return s === "pending_superadmin";
    return false;
  })();

  // Attachments: visible to principal, superadmin, and the creator (not plain teacher viewing other requests)
  const canSeeAttachments =
    role === "superadmin" ||
    role === "principal" ||
    role === "admin" ||
    role === "administrator" ||
    request.created_by_role === role; // creator always sees their own

  const approverLabel =
    role === "admin" || role === "administrator" ? "Admin Review"
    : role === "principal" ? "Principal Review"
    : role === "superadmin" ? "Superadmin Review"
    : "";

  const doAction = async (action) => {
    if (action === "reject" && !remark.trim()) {
      showToast("error", "Remark Required", "Please provide a reason for rejection.");
      return;
    }
    setActing(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/procurement/${request.id}/action`, {
        method: "POST",
        headers: getJsonAuthHeaders(),
        body: JSON.stringify({
          action,
          remark: remark.trim() || null,
          approved_amount: parseFloat(approvedAmount) || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Action failed");
      }
      const done = { approve: "Approved", reject: "Rejected", return: "Returned" };
      showToast("success", done[action] || "Done", `Request ${request.ref_number} updated.`);
      onRefresh();
      onClose();
    } catch (err) {
      showToast("error", "Error", err.message);
    } finally {
      setActing(false);
    }
  };

  const handlePrint = () => {
    const el = document.getElementById("proc-formal-letter");
    if (!el) return;
    // Use absolute origin for logo so it loads in the print window
    const absoluteLogoUrl = `${window.location.origin}/als-logo.png`;
    const w = window.open("", "_blank", "width=900,height=700");
    // Swap the logo src to absolute in the printed copy
    const printHtml = el.innerHTML.replace(/src="\/als-logo\.png"/g, `src="${absoluteLogoUrl}"`);
    w.document.write(`<!DOCTYPE html><html><head>
      <title>Procurement Letter — ${request.ref_number}</title>
      <style>
        @page { size: A4; margin: 2cm; }
        body { font-family:'Times New Roman',serif; color:#111; margin:0; padding:0; }
        table { width:100%; border-collapse:collapse; }
        th,td { border:1px solid #bbb; padding:5px 10px; font-size:13px; }
        th { background:#f5f5f5; }
        hr { border:none; border-top:2.5px solid #111; margin:10px 0 14px; }
        p { white-space:pre-wrap; }
      </style>
    </head><body>${printHtml}</body></html>`);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 400);
  };

  const canViewLetter =
    request.letter_body ||
    request.status === "approved" ||
    request.status === "pending_superadmin" ||
    request.status === "pending_principal";

  return (
    <div
      className="proc-modal-overlay"
      onClick={(e) => e.target === e.currentTarget && !showLetter && onClose()}
    >
      <div className="proc-modal" style={{ maxWidth: 760 }}>

        {/* ── Header ── */}
        <div className="proc-modal-header">
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 className="proc-modal-title" style={{ wordBreak: "break-word" }}>
              {request.subject}
            </h3>
            <p className="proc-modal-sub" style={{ display: "flex", gap: ".5rem", flexWrap: "wrap", alignItems: "center" }}>
              <span style={{ fontFamily: "monospace", color: "var(--proc-purple)", fontWeight: 600 }}>
                {request.ref_number}
              </span>
              <span>·</span>
              <span>{request.campus_name || "N/A"}</span>
              <span>·</span>
              <StatusBadge status={request.status} />
            </p>
          </div>
          <div style={{ display: "flex", gap: ".4rem", alignItems: "flex-start", flexShrink: 0 }}>
            {canViewLetter && (
              <button
                className="proc-btn proc-btn-outline proc-btn-sm"
                onClick={() => setShowLetter(!showLetter)}
              >
                <MdDescription size={14} /> {showLetter ? "Hide Letter" : "View Letter"}
              </button>
            )}
            {canViewLetter && (
              <button className="proc-btn proc-btn-outline proc-btn-sm" onClick={handlePrint}>
                <MdPrint size={14} /> Print
              </button>
            )}
            <button className="proc-modal-close" onClick={onClose} aria-label="Close">
              <MdClose />
            </button>
          </div>
        </div>

        {/* ── Formal Letter preview (with editable fields) ── */}
        {showLetter && (
          <div style={{
            border: "1.5px solid var(--proc-gray-200)",
            borderRadius: 8,
            marginBottom: "1.2rem",
            background: "#fff",
            overflow: "hidden",
          }}>
            {/* Editable fields — only for non-superadmin */}
            {role !== "superadmin" && (
              <div style={{
                background: "var(--proc-gray-50)",
                borderBottom: "1px solid var(--proc-gray-200)",
                padding: ".9rem 1.2rem",
              }}>
                <div style={{ fontSize: ".72rem", textTransform: "uppercase", letterSpacing: ".06em", color: "var(--proc-gray-600)", fontWeight: 700, marginBottom: ".65rem" }}>
                  Edit Letter Content
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: ".55rem" }}>
                  <div>
                    <label style={{ fontSize: ".75rem", fontWeight: 600, color: "var(--proc-gray-600)", display: "block", marginBottom: ".2rem" }}>Salutation</label>
                    <input
                      className="proc-input"
                      value={letterEdits.salutation}
                      onChange={(e) => setLetterEdits((p) => ({ ...p, salutation: e.target.value }))}
                      style={{ padding: ".38rem .7rem", fontSize: ".87rem" }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: ".75rem", fontWeight: 600, color: "var(--proc-gray-600)", display: "block", marginBottom: ".2rem" }}>Body Paragraph</label>
                    <textarea
                      className="proc-textarea"
                      value={letterEdits.body}
                      onChange={(e) => setLetterEdits((p) => ({ ...p, body: e.target.value }))}
                      rows={4}
                      style={{ padding: ".38rem .7rem", fontSize: ".87rem", width: "100%", boxSizing: "border-box", fontFamily: "'Times New Roman', Georgia, serif", whiteSpace: "pre-wrap" }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: ".75rem", fontWeight: 600, color: "var(--proc-gray-600)", display: "block", marginBottom: ".2rem" }}>Closing Paragraph</label>
                    <input
                      className="proc-input"
                      value={letterEdits.closing}
                      onChange={(e) => setLetterEdits((p) => ({ ...p, closing: e.target.value }))}
                      style={{ padding: ".38rem .7rem", fontSize: ".87rem" }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Superadmin — read-only note */}
            {role === "superadmin" && (
              <div style={{
                background: "#f0f4ff",
                borderBottom: "1px solid #c7d5f5",
                padding: ".65rem 1.2rem",
                fontSize: ".78rem", color: "#3a5cbf", fontWeight: 500,
                display: "flex", alignItems: "center", gap: ".4rem",
              }}>
                🔒 View only — the letter is as submitted by the requester.
              </div>
            )}

            {/* Letter preview */}
            <div style={{ padding: "1.5rem" }}>
              <FormalLetter request={request} editableContent={role === "superadmin" ? {} : letterEdits} />
            </div>
          </div>
        )}

        {/* ── Items ── */}
        <div className="proc-detail-section">
          <div className="proc-detail-section-title">
            <MdOutlineBarChart size={14} /> ITEMS &amp; PRICES
          </div>
          {(request.items || []).map((it, i) => (
            <div key={i} className="proc-items-view-row">
              <span>{i + 1}.&nbsp;{it.item}</span>
              <span style={{ fontWeight: 700, color: "var(--proc-purple)" }}>{fmtPKR(it.price)}</span>
            </div>
          ))}
          <div className="proc-items-view-total">
            <span>Total Requested</span>
            <span>{fmtPKR(request.total_amount)}</span>
          </div>
          {request.approved_amount != null && (
            <div className="proc-items-view-total" style={{ color: "var(--proc-green)" }}>
              <span>Approved Amount</span>
              <span>{fmtPKR(request.approved_amount)}</span>
            </div>
          )}
        </div>

        {/* ── Description ── */}
        {request.description && (
          <div className="proc-detail-section">
            <div className="proc-detail-section-title">
              <MdDescription size={14} /> DESCRIPTION
            </div>
            <p style={{ margin: 0, fontSize: ".88rem", lineHeight: 1.7, color: "var(--proc-gray-800)" }}>
              {request.description}
            </p>
          </div>
        )}

        {/* ── Letter from requester — shown to all approvers as formatted letter ── */}
        {request.letter_body && (
          <div className="proc-detail-section">
            <div className="proc-detail-section-title" style={{ justifyContent: "space-between" }}>
              <span style={{ display: "flex", alignItems: "center", gap: ".3rem" }}>
                <MdDescription size={14} /> LETTER FROM {(request.created_by_role || "REQUESTER").toUpperCase()}
              </span>
              <div style={{ display: "flex", gap: ".4rem" }}>
                <button
                  type="button"
                  className="proc-btn proc-btn-outline proc-btn-sm"
                  onClick={() => setShowLetter(!showLetter)}
                >
                  <MdDescription size={13} /> {showLetter ? "Collapse" : "Full View"}
                </button>
                <button
                  type="button"
                  className="proc-btn proc-btn-outline proc-btn-sm"
                  onClick={handlePrint}
                >
                  <MdPrint size={13} /> Print
                </button>
              </div>
            </div>

            {/* Compact ALS letter view — always visible */}
            <div style={{
              border: "1px solid var(--proc-gray-200)",
              borderRadius: 6,
              overflow: "hidden",
              background: "#fff",
            }}>
              {/* Letterhead with logo from public folder */}
              <div style={{
                background: "var(--proc-gray-50)",
                borderBottom: "1px solid var(--proc-gray-200)",
                padding: ".7rem 1rem",
                display: "flex", alignItems: "center", gap: 12,
              }}>
                <img
                  src="/als-logo.png"
                  alt="ALS"
                  style={{ width: 42, height: 42, objectFit: "contain", flexShrink: 0 }}
                  onError={(e) => { e.target.style.display = "none"; }}
                />
                <div style={{ flex: 1, textAlign: "center" }}>
                  <div style={{ fontSize: 15, fontWeight: "bold", fontFamily: "'Times New Roman', serif", letterSpacing: ".02em" }}>
                    AMERICAN LYCETUFF SCHOOLS
                  </div>
                  <div style={{ fontSize: 11, color: "#666", fontFamily: "'Times New Roman', serif" }}>
                    {request.campus_name} · Procurement Request Letter
                  </div>
                </div>
                <div style={{ fontSize: 11, color: "#666", textAlign: "right", flexShrink: 0 }}>
                  <div><strong>Ref:</strong> {request.ref_number}</div>
                  <div><strong>Date:</strong> {fmtDate(request.created_at)}</div>
                </div>
              </div>
              <div style={{ borderBottom: "2px solid #111", margin: "0" }} />

              {/* Letter body */}
              <div style={{ padding: "1rem 1.3rem", fontFamily: "'Times New Roman', Georgia, serif" }}>
                <div style={{ fontSize: 13, fontWeight: "bold", textDecoration: "underline", marginBottom: 12 }}>
                  Subject: {request.subject}
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.9, marginBottom: 10 }}>
                  Respected Sir / Madam,
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.9, whiteSpace: "pre-wrap", color: "#222" }}>
                  {request.letter_body}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Attachments — clickable: images open in lightbox, others download ── */}
        {canSeeAttachments && (request.attachments || []).length > 0 && (
          <div className="proc-detail-section">
            <div className="proc-detail-section-title">
              <MdAttachFile size={14} /> ATTACHED DOCUMENTS ({request.attachments.length})
            </div>
            <div className="proc-file-list">
              {request.attachments.map((att, i) => {
                const img = isImage(att);
                const thumbSrc = img ? imageBlobUrls[att.path] : null;
                return (
                  <div key={i}>
                    {/* File row */}
                    <div
                      className="proc-file-item"
                      style={{ cursor: "pointer" }}
                      onClick={() => openAttachment(att)}
                      title={img ? "Click to view image" : "Click to download"}
                    >
                      <FiFileText size={13} />
                      <span style={{ flex: 1, color: "var(--proc-purple)", textDecoration: "underline" }}>
                        {att.filename}
                      </span>
                      <span style={{ fontSize: ".7rem", color: "var(--proc-gray-400)", marginLeft: ".5rem" }}>
                        {fmtDate(att.uploaded_at)}
                      </span>
                      <span style={{ fontSize: ".7rem", color: "var(--proc-gray-400)", marginLeft: ".5rem" }}>
                        {img ? "🖼 View" : "⬇ Download"}
                      </span>
                    </div>

                    {/* Inline thumbnail for images — loaded via blob to include auth */}
                    {img && thumbSrc && (
                      <div style={{ paddingLeft: "1.5rem", paddingBottom: ".4rem" }}>
                        <img
                          src={thumbSrc}
                          alt={att.filename}
                          style={{
                            maxWidth: 220, maxHeight: 160,
                            objectFit: "cover", borderRadius: 6,
                            border: "1.5px solid var(--proc-gray-200)",
                            cursor: "pointer", display: "block",
                            marginTop: ".35rem",
                          }}
                          onClick={() => setLightboxSrc(thumbSrc)}
                        />
                      </div>
                    )}
                    {img && !thumbSrc && (
                      <div style={{ paddingLeft: "1.5rem", paddingTop: ".3rem", paddingBottom: ".4rem" }}>
                        <span style={{ fontSize: ".74rem", color: "var(--proc-gray-400)" }}>Loading preview…</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Image lightbox ── */}
        {lightboxSrc && (
          <div
            style={{
              position: "fixed", inset: 0, background: "rgba(0,0,0,.82)",
              display: "flex", alignItems: "center", justifyContent: "center",
              zIndex: 2000, padding: "1rem",
            }}
            onClick={() => setLightboxSrc(null)}
          >
            <button
              onClick={() => setLightboxSrc(null)}
              style={{
                position: "absolute", top: "1.2rem", right: "1.5rem",
                background: "rgba(255,255,255,.15)", border: "none", color: "#fff",
                borderRadius: "50%", width: 36, height: 36,
                fontSize: "1.3rem", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
              aria-label="Close"
            >
              <MdClose />
            </button>
            <img
              src={lightboxSrc}
              alt="Attachment"
              style={{ maxWidth: "90vw", maxHeight: "88vh", borderRadius: 8, boxShadow: "0 8px 40px rgba(0,0,0,.5)" }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        {/* ── Audit Trail ── */}
        <div className="proc-detail-section">
          <div className="proc-detail-section-title">AUDIT TRAIL</div>
          <AuditTrail entries={request.audit_trail} />
        </div>

        {/* ── Action Panel (for approvers) ── */}
        {canAct && (
          <div className="proc-action-panel">
            <div className="proc-action-panel-title">{approverLabel}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".85rem", marginBottom: ".8rem" }}>
              <div className="proc-form-group" style={{ marginBottom: 0 }}>
                <label className="proc-form-label">AMOUNT TO APPROVE (PKR)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  className="proc-input"
                  value={approvedAmount}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/[^0-9.]/g, "");
                    const parts = raw.split(".");
                    setApprovedAmount(parts.length > 2 ? parts[0] + "." + parts.slice(1).join("") : raw);
                  }}
                />
              </div>
              <div className="proc-form-group" style={{ marginBottom: 0 }}>
                <label className="proc-form-label">
                  REMARK{" "}
                  <span style={{ color: "var(--proc-gray-400)", fontWeight: 400, textTransform: "none" }}>
                    (required to reject)
                  </span>
                </label>
                <input
                  type="text"
                  className="proc-input"
                  placeholder="Optional note or reason..."
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                />
              </div>
            </div>

            <div className="proc-action-buttons">
              <button className="proc-btn proc-btn-outline" onClick={() => doAction("return")} disabled={acting}>
                <MdUndo size={15} /> Return to Sender
              </button>
              <button className="proc-btn proc-btn-danger" onClick={() => doAction("reject")} disabled={acting}>
                <MdCancel size={15} /> Reject
              </button>
              <button className="proc-btn proc-btn-success" onClick={() => doAction("approve")} disabled={acting}>
                <MdCheckCircle size={15} />
                {role === "superadmin"
                  ? "Final Approve"
                  : role === "principal"
                  ? "Approve & Forward to Superadmin"
                  : "Approve & Forward to Principal"}
              </button>
            </div>
          </div>
        )}

        {/* Hidden letter for printing — uses current editable content */}
        <div style={{ display: "none" }}>
          <div id="proc-formal-letter">
            <FormalLetter request={request} editableContent={letterEdits} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main ProcurementPage ──────────────────────────────────────────────────────

export default function ProcurementPage({ showToast: externalToast }) {
  const role = (localStorage.getItem("als_role") || "").toLowerCase();

  const [localToast, setLocalToast] = useState(null);
  const showToast = useCallback(
    (type, title, message) => {
      if (externalToast) { externalToast(type, title, message); return; }
      setLocalToast({ type, title, message });
      setTimeout(() => setLocalToast(null), 3500);
    },
    [externalToast]
  );

  const [requests, setRequests]   = useState([]);
  const [stats, setStats]         = useState(null);
  const [loading, setLoading]     = useState(true);
  const [campuses, setCampuses]   = useState([]);
  const [showNewModal, setShowNewModal]   = useState(false);
  const [selectedReq, setSelectedReq]    = useState(null);
  const [activeTab, setActiveTab]        = useState("all");
  const [search, setSearch]              = useState("");
  const [campusFilter, setCampusFilter]  = useState("");

  const tabs = [
    { id: "all",      label: "All" },
    ...(role !== "teacher" ? [{ id: "awaiting", label: "Awaiting Me" }] : []),
    { id: "approved", label: "Approved" },
    { id: "rejected", label: "Rejected" },
    ...(role === "teacher" ? [{ id: "returned", label: "Returned" }] : []),
  ];

  const awaitingStatus = {
    admin:         "pending_admin",
    administrator: "pending_admin",
    principal:     "pending_principal",
    superadmin:    "pending_superadmin",
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, sRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/procurement`,       { headers: getAuthHeaders() }),
        fetch(`${API_BASE_URL}/api/procurement-stats`, { headers: getAuthHeaders() }),
      ]);
      if (rRes.ok) setRequests(await rRes.json());
      if (sRes.ok) setStats(await sRes.json());
    } catch (err) {
      console.error("Procurement fetch error", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCampuses = useCallback(async () => {
    if (role !== "superadmin") return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/campuses`, { headers: getAuthHeaders() });
      if (res.ok) setCampuses(await res.json());
    } catch (_) {}
  }, [role]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => { fetchAll(); fetchCampuses(); }, []);

  const filtered = requests.filter((r) => {
    if (activeTab === "awaiting") {
      const expected = awaitingStatus[role];
      if (expected && r.status !== expected) return false;
    } else if (activeTab === "approved") {
      if (r.status !== "approved") return false;
    } else if (activeTab === "rejected") {
      if (r.status !== "rejected") return false;
    } else if (activeTab === "returned") {
      if (r.status !== "returned") return false;
    }
    if (campusFilter && String(r.campus_id) !== campusFilter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      if (
        !r.subject.toLowerCase().includes(q) &&
        !r.ref_number.toLowerCase().includes(q) &&
        !(r.campus_name || "").toLowerCase().includes(q) &&
        !(r.created_by_name || "").toLowerCase().includes(q)
      )
        return false;
    }
    return true;
  });

  const canCreate = ["teacher", "admin", "administrator", "principal"].includes(role);

  return (
    <div className="proc-page dashboard-content">

      {/* ── Header ── */}
      <div className="proc-page-header">
        <div className="proc-page-header-left">
          <div className="proc-page-icon"><FiFileText size={20} /></div>
          <div>
            <h2 className="proc-page-title">Procurement Filing</h2>
            <p className="proc-page-sub">
              {role === "teacher"
                ? "Submit procurement requests to your campus Admin."
                : role === "admin" || role === "administrator"
                ? "Review campus requests and forward approved ones to Principal."
                : role === "principal"
                ? "Review Admin-approved requests and forward to Superadmin."
                : "Monitor all campus procurement requests across all campuses."}
            </p>
          </div>
        </div>
        <div className="proc-header-actions">
          {canCreate && (
            <button className="proc-btn proc-btn-primary" onClick={() => setShowNewModal(true)}>
              <MdAdd size={16} /> New Request
            </button>
          )}
          <button className="proc-btn proc-btn-outline" onClick={fetchAll} disabled={loading}>
            <MdRefresh size={16} /> Refresh
          </button>
        </div>
      </div>

      {/* ── Count cards ── */}
      {stats && (
        <div className="proc-count-row">
          {[
            { label: "Total Files",  value: stats.total_files, icon: <FiFileText /> },
            { label: "Drafts",       value: stats.drafts,      icon: "✎" },
            { label: "Approved",     value: stats.approved,    icon: <MdCheckCircle />, color: "var(--proc-green)" },
            { label: "Rejected",     value: stats.rejected,    icon: <MdCancel />,      color: "var(--proc-red)" },
          ].map((c) => (
            <div key={c.label} className="proc-count-card">
              <div className="proc-count-label">{c.label}</div>
              <div className="proc-count-value" style={c.color ? { color: c.color } : {}}>
                {c.value}
              </div>
              <span className="proc-count-icon">{c.icon}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── PKR stat cards ── */}
      {stats && (
        <div className="proc-stats-row">
          <div className="proc-stat-card purple">
            <div className="proc-stat-label">Total Requested</div>
            <div className="proc-stat-value">{fmtPKR(stats.total_requested)}</div>
            <MdOutlineBarChart className="proc-stat-icon" />
          </div>
          <div className="proc-stat-card green">
            <div className="proc-stat-label">Total Approved</div>
            <div className="proc-stat-value">{fmtPKR(stats.total_approved)}</div>
            <MdCheckCircle className="proc-stat-icon" />
          </div>
          <div className="proc-stat-card red">
            <div className="proc-stat-label">Total Rejected</div>
            <div className="proc-stat-value">{fmtPKR(stats.total_rejected)}</div>
            <MdCancel className="proc-stat-icon" />
          </div>
          {stats.pending > 0 && (
            <div className="proc-stat-card amber">
              <div className="proc-stat-label">Pending Review</div>
              <div className="proc-stat-value">{stats.pending}</div>
              <MdFilterList className="proc-stat-icon" />
            </div>
          )}
        </div>
      )}

      {/* ── Superadmin campus breakdown ── */}
      {role === "superadmin" && stats && (stats.campus_breakdown || []).length > 0 && (
        <div className="proc-campus-section" style={{ marginBottom: "1.5rem" }}>
          <div className="proc-campus-section-header">
            <MdOutlineBarChart size={18} style={{ color: "var(--proc-purple)" }} />
            EXPENSES BY CAMPUS
          </div>
          <table className="proc-campus-table">
            <thead>
              <tr>
                <th>Campus</th>
                <th style={{ textAlign: "right" }}>Files</th>
                <th style={{ textAlign: "right" }}>Requested (PKR)</th>
                <th style={{ textAlign: "right" }}>Approved (PKR)</th>
                <th style={{ textAlign: "right" }}>Rejected (PKR)</th>
              </tr>
            </thead>
            <tbody>
              {stats.campus_breakdown.map((c) => (
                <tr
                  key={c.campus_id}
                  style={{ cursor: "pointer" }}
                  onClick={() => setCampusFilter(campusFilter === String(c.campus_id) ? "" : String(c.campus_id))}
                >
                  <td>{c.campus_name}</td>
                  <td style={{ textAlign: "right" }}>{c.files}</td>
                  <td style={{ textAlign: "right" }} className="num">{Number(c.requested).toLocaleString("en-PK")}</td>
                  <td style={{ textAlign: "right" }} className="approve-num">{Number(c.approved).toLocaleString("en-PK")}</td>
                  <td style={{ textAlign: "right" }} className="reject-num">{Number(c.rejected).toLocaleString("en-PK")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Toolbar ── */}
      <div className="proc-toolbar">
        <div className="proc-search-wrap">
          <MdSearch size={18} />
          <input
            placeholder="Search by subject, ref, campus or name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--proc-gray-400)", padding: 0 }}
            >
              <MdClose size={15} />
            </button>
          )}
        </div>

        <div className="proc-tab-row">
          {tabs.map((t) => (
            <button
              key={t.id}
              className={`proc-tab ${activeTab === t.id ? "active" : ""}`}
              onClick={() => setActiveTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {role === "superadmin" && campuses.length > 0 && (
          <div className="proc-campus-filter">
            <select value={campusFilter} onChange={(e) => setCampusFilter(e.target.value)}>
              <option value="">All Campuses</option>
              {campuses.map((c) => (
                <option key={c.id} value={String(c.id)}>{c.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* ── Cards ── */}
      {loading ? (
        <div className="proc-empty"><p>Loading procurement requests…</p></div>
      ) : filtered.length === 0 ? (
        <div className="proc-empty">
          <FiFileText />
          <h3>No requests found</h3>
          <p>
            {activeTab === "all" && canCreate
              ? 'Click "New Request" to submit your first procurement request.'
              : "No requests match the current filter."}
          </p>
        </div>
      ) : (
        <div className="proc-cards-grid">
          {filtered.map((req) => (
            <div
              key={req.id}
              className="proc-card"
              onClick={() => setSelectedReq(req)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && setSelectedReq(req)}
            >
              <div className="proc-card-header">
                <div className="proc-card-subject">{req.subject}</div>
                <StatusBadge status={req.status} />
              </div>
              {req.campus_name && (
                <div className="proc-card-campus">🏫 {req.campus_name}</div>
              )}
              {req.created_by_name && role !== "teacher" && (
                <div style={{ fontSize: ".77rem", color: "var(--proc-gray-600)", marginBottom: ".25rem" }}>
                  Filed by: <strong>{req.created_by_name}</strong>
                  {" "}({req.created_by_role})
                </div>
              )}
              <TrackDots status={req.status} />
              <div className="proc-card-meta">
                <div className="proc-card-amount">{fmtPKR(req.total_amount)}</div>
                <div style={{ textAlign: "right" }}>
                  <div className="proc-card-date">{fmtDate(req.created_at)}</div>
                  <div className="proc-card-ref">{req.ref_number}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Modals ── */}
      {showNewModal && (
        <NewRequestModal
          role={role}
          onClose={() => setShowNewModal(false)}
          onSaved={fetchAll}
          showToast={showToast}
        />
      )}

      {selectedReq && (
        <DetailModal
          request={selectedReq}
          role={role}
          onClose={() => setSelectedReq(null)}
          onRefresh={fetchAll}
          showToast={showToast}
        />
      )}

      {/* ── Local toast fallback ── */}
      {localToast && (
        <div
          className={`toast ${localToast.type}`}
          style={{ position: "fixed", bottom: "1.5rem", right: "1.5rem", zIndex: 9999, minWidth: 280 }}
        >
          <div style={{ padding: ".9rem 1.2rem" }}>
            <strong>{localToast.title}</strong>
            <p style={{ margin: ".2rem 0 0", fontSize: ".85rem" }}>{localToast.message}</p>
          </div>
        </div>
      )}
    </div>
  );
}
